import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	TFileId,
	TLanguageIDs,
	IItemForGetShortInfo,
	IDefaultLanguage,
	IUpdateCategoryV2,
	IGetCategoryInfoV2,
} from 'types';
import {
	editCategoryInitialState,
	EditCategoryContext,
	TTCategoryType,
	TTypeOfModalWindow,
} from './editCategoryContext';
import { editCategoryReducer } from './editCategoryReducer';
import { EditCategoryActionTypes } from './editCategoryActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getCategoryInfoV2 } from 'backend/categories';

export const EditCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editCategoryReducer, editCategoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const categoryID = params.get('categoryID');

	const { response, loading, refetch } = useFetchData<IGetCategoryInfoV2, string>(
		categoryID ? getCategoryInfoV2 : null,
		categoryID || '',
	);

	useNamedHeader(response?.data.name);

	useEffect(() => {
		if (response) {
			const translations = response.data.translations.map((translation) => ({
				languageId: translation.languageId,
				name: translation.name,
			}));

			dispatch({
				type: EditCategoryActionTypes.SET_ALL_DATA,
				payload: {
					categoryID: categoryID ? categoryID : '',
					filePath: response.data.photoPath,
					initialIDSelectedCustomer: response.data.customer.id,
					initialIDsSelectedRestaurant: response.data.restaurants.map((restaurant) => restaurant.id),
					nameOfCategory: response.data.name || '',
					categoryType: response.data.type,
					isRestricted: response.data.isRestricted,
					hasSchedulePriority: response.data.overrideScheduleAvailability,
					activationSchedule: response.data.activationCron || '',
					deactivationSchedule: response.data.deactivationCron || '',
					translations,
				},
			});
		}
	}, [categoryID, response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditCategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditCategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditCategoryActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditCategoryActionTypes.DELETE_PHOTO }), []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditCategoryActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditCategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditCategoryActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: EditCategoryActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const changeNameOfCategory = useCallback((value: string) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_NAME_OF_CATEGORY,
			payload: {
				nameOfCategory: value,
			},
		});
	}, []);

	const setCategoryType = useCallback((value: TTCategoryType) => {
		dispatch({
			type: EditCategoryActionTypes.SET_CATEGORY_TYPE,
			payload: {
				categoryType: value,
			},
		});
	}, []);

	const toggleIsRestricted = useCallback(() => {
		dispatch({
			type: EditCategoryActionTypes.TOGGLE_IS_RESTRICTED,
		});
	}, []);

	const toggleHasSchedulePriority = useCallback(() => {
		dispatch({
			type: EditCategoryActionTypes.TOGGLE_HAS_SCHEDULE_PRIORITY,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditCategoryActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateCategoryV2>) => {
		dispatch({
			type: EditCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditCategoryContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				setFileId,
				deletePhoto,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfCategory,
				setCategoryType,
				toggleIsRestricted,
				toggleHasSchedulePriority,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				isLoading: state.isLoading,
				filePath: state.filePath,
				fileId: state.fileId,
				categoryID: state.categoryID,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfCategory: state.nameOfCategory,
				categoryType: state.categoryType,
				isRestricted: state.isRestricted,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditCategoryContext.Provider>
	);
};
